import { z } from 'zod';

// Types
export const FeatureFlagSetStruct = z.object({
  // This is a utility flag. It is used to determine if the FeatureFlagProvider has successfully
  // fetched flags from LaunchDarkly at least once
  doNotRemoveFeatureFlagProviderReady: z.boolean(),

  assignByStrategy: z.boolean(),
  assignByIndividualReceivable: z.boolean(),
  displayAssignment: z.boolean(),
  displayPortfolioCovenants: z.boolean(),
  showBorrowerReceivableDashboard: z.boolean(),
  showGraphMonitor: z.boolean(), // controls v1 for data pipeline
  showDataPipeline: z.boolean(), // controls v2 for data pipeline
  enableUndoPipelineRun: z.boolean(),

  // for our asset verification only customers, we want to let them
  // upload loan-tapes but nothing else. This flag will be set for those
  enableManualUploaderOnly: z.boolean(),
  // for borrower graphs that are not compatible with manual uploader
  hideManualUploader: z.boolean(),
  // facilities to hide in the dashboard
  creditFacilitySlugsToHide: z.array(z.string()),

  isStatementsMonthlySummaryEnabled: z.boolean(),
  displayLenderPortfolioMonitoringCovenants: z.boolean(),
  displayLenderPortfolioMonitoringReceivables: z.boolean(),

  /**
   * This flag drives "strats" tab for specific facility view for lenders.
   * This is independent of portfolio level strats for lenders.
   *
   * Note this flag is "string" and not "boolean" to help drive
   * the various strats versioning: Explo, Quicksights, None.
   * Available values are: "explo", "quicksight", "hide"
   */
  showLenderFacilitiesStrats: z.enum(['hide', 'quicksight', 'explo']),

  /**
   * This flag drives "strats" tab for overall portfolio view for lenders.
   * This is independent of specific facility level strats for lenders.
   * This will only drive Explo "strats", thus can use boolean
   */
  showLenderPortfolioOverviewStrats: z.boolean(),

  // This flag drives the full "strats" page.
  // This flag is very similar to the one above, is "string" to
  // drive various strats versions: Explo, Quicksights, None.
  // Available values are: "explo", "quicksight", "hide"
  showStratsPage: z.enum(['hide', 'quicksight', 'explo']),

  // Flag drives showing Explo Report Builder feature in respective areas.
  // Lender: Facility Portfolio Tab
  // Borrower: Strats Page
  showExploReportBuilder: z.boolean(),

  displayLenderSettingsFacilities: z.boolean(),

  // should we let lender edit baseFields?
  enableLenderBaseFieldEdits: z.boolean(),

  // temporary flags until @JonAtFinley's work on uiVisibility is complete
  isBorrowerLimitedOrganization: z.boolean(),
  lenderOwnerOrganizationWithBorrowerLimited: z.boolean(),

  // v4 deliverables
  showV4Deliverables: z.boolean(),

  // VB Lender View
  isValleyBankLenderView: z.boolean(),

  // Target server
  targetHeaderValue: z.string(),

  isAthenaEnabled: z.boolean(),
  isExcelEngineAdvanceRateEnabled: z.boolean(),

  showBorrowerRearchitecture: z.boolean(),

  // temp flag
  // see comment below
  fundingRequestV2HistoricalDetail: z.boolean(),

  // Temporary flag for expense authoring rollout - TODO: @sachinr4 to deprecate
  allowExpenseAuthoring: z.boolean(),

  enableCrossTabLogout: z.boolean(),
});
export type FeatureFlagSet = z.infer<typeof FeatureFlagSetStruct>;

// Constants
/**
 * REMINDER: when updating this list, please also update "FeatureFlagSet" types above
 *
 * We should look to improve process of setting flag types and defaults.
 * Initially basing off of below (dated) issue on one avenue of managing this.
 * https://github.com/launchdarkly/js-sdk-common/issues/32
 *
 * One key downfall of this method is that it doesn't prevent users to add / access
 * flags that aren't inluded in the type. So if type doesn't include "unavailable",
 * compiler won't complain about "flags.unavailable" (it'll treat it as "any" type),
 * or any lingering flags as well.
 * This possibly may be resolved once we update our LD package version.
 *
 * Note: at the moment, we use a single instance of LaunchDarkly for both our
 * "admin" and "core" apps, implying that both apps technically have access to
 * the same flags. If we do split up LD usage, should also split this structure
 */
export const defaultFeatureFlags: FeatureFlagSet = {
  doNotRemoveFeatureFlagProviderReady: false,

  assignByStrategy: false,
  assignByIndividualReceivable: false,
  displayAssignment: false,
  displayPortfolioCovenants: false,
  showBorrowerReceivableDashboard: false,
  showGraphMonitor: false,
  showDataPipeline: false,
  enableUndoPipelineRun: false,

  enableManualUploaderOnly: false,
  hideManualUploader: false,
  creditFacilitySlugsToHide: [],

  isStatementsMonthlySummaryEnabled: false,
  displayLenderPortfolioMonitoringCovenants: false,
  displayLenderPortfolioMonitoringReceivables: false,

  showLenderFacilitiesStrats: 'hide',
  showLenderPortfolioOverviewStrats: false,
  showStratsPage: 'hide',

  showExploReportBuilder: false,

  displayLenderSettingsFacilities: false,

  enableLenderBaseFieldEdits: false,

  isBorrowerLimitedOrganization: false,
  lenderOwnerOrganizationWithBorrowerLimited: false,

  showV4Deliverables: false,

  isValleyBankLenderView: false,

  targetHeaderValue: '',

  isAthenaEnabled: false,
  isExcelEngineAdvanceRateEnabled: false,

  // temporary FF for borrower dashboard rearchitecture to rearrange routes to more similarly match lender dashboard
  showBorrowerRearchitecture: false,

  // temporary FF to display funding request detail page v1 (if false) and v2 (if true) Mostly has to do with lender, but will also eventually be used for borrowers as well. Will be GA, just putting this FF in incase need to quickly roll back
  fundingRequestV2HistoricalDetail: false,

  allowExpenseAuthoring: false,

  enableCrossTabLogout: false,
};
